import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { ChevronLeft, ChevronRight, Images, X } from "lucide-react";
import { API } from "@/context/AuthContext";
import { useT } from "@/i18n/LocaleContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PracticeGallery() {
  const { t, dict, locale } = useT();
  const TABS = dict.gallery.tabs.map(([key, label]) => ({ key, label }));
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState(TABS[0].key);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/galerie`)
      .then((r) => { if (active) setItems(r.data); })
      .catch(() => { if (active) setItems([]); });
    return () => { active = false; };
  }, []);

  const filtered = items ? items.filter((i) => i.kategorie === tab) : [];

  const close = useCallback(() => setSelected(null), []);
  const step = useCallback(
    (dir) => setSelected((s) => (s === null || !filtered.length ? s : (s + dir + filtered.length) % filtered.length)),
    [filtered]
  );

  useEffect(() => {
    if (selected === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") step(-1);
      if (e.key === "ArrowRight") step(1);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [selected, close, step]);

  if (!items) return null;

  const current = selected !== null ? filtered[selected] : null;

  return (
    <section id="praxis" className="py-24 lg:py-32 bg-white" data-testid="praxis-section">
      <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="font-tech text-[11px] tracking-[0.3em] uppercase text-[var(--ci-blue)] mb-6">{t("gallery.eyebrow")}</p>
          <h2 className="text-3xl lg:text-5xl font-bold tracking-tight max-w-3xl text-[var(--ci-grey)]">
            {t("gallery.title1")}<span className="text-[var(--ci-blue)]">{t("gallery.title2")}</span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-[var(--ci-grey)] leading-relaxed max-w-2xl">
            {t("gallery.copy")}
          </p>
        </motion.div>

        <div className="mt-12 flex gap-8 border-b border-[#E3E8EC]" role="tablist" aria-label={locale === "en" ? "Gallery categories" : "Galerie-Bereiche"} data-testid="praxis-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              role="tab"
              aria-selected={tab === t.key}
              data-testid={`praxis-tab-${t.key}`}
              onClick={() => { setTab(t.key); setSelected(null); }}
              className={`relative pb-4 font-tech text-[11px] sm:text-xs tracking-[0.22em] uppercase transition-colors duration-200 ${
                tab === t.key ? "text-[var(--ci-blue)]" : "text-[var(--ci-grey)] hover:text-[var(--ci-turquoise)]"
              }`}
            >
              {t.label}
              {tab === t.key && (
                <motion.span
                  layoutId="praxis-tab-underline"
                  className="absolute left-0 right-0 -bottom-px h-[2px] bg-[var(--ci-blue)]"
                  transition={{ type: "spring", stiffness: 400, damping: 32 }}
                />
              )}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
            className="mt-8"
          >
            {filtered.length === 0 ? (
              <div
                className="rounded-2xl border border-dashed border-[#C3CCD4] bg-[#F7F9FA] py-16 text-center"
                data-testid={`gallery-empty-${tab}`}
              >
                <Images size={28} className="mx-auto text-[#C3CCD4]" />
                <p className="mt-4 text-sm text-[var(--ci-grey)]">{t("gallery.empty")}</p>
              </div>
            ) : (
              <div className="rounded-2xl border border-[#E3E8EC] bg-[#EEF1F4] p-1 overflow-hidden">
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-1" data-testid={`gallery-grid-${tab}`}>
                  {filtered.map((item, i) => (
                    <motion.figure
                      key={item.id}
                      initial={{ opacity: 0, scale: 0.96 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ duration: 0.45, delay: (i % 3) * 0.06 }}
                      className="group relative aspect-[4/3] overflow-hidden rounded-lg bg-white cursor-pointer"
                      data-testid={`gallery-item-${tab}-${i}`}
                      role="button"
                      tabIndex={0}
                      aria-label={`${locale === "en" ? "Enlarge image:" : "Bild vergrößern:"} ${item.titel}`}
                      onClick={() => setSelected(i)}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected(i); } }}
                    >
                      <img
                        src={`${BACKEND}${item.bild_url}`}
                        alt={item.titel}
                        loading="lazy"
                        className="absolute inset-0 block h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#111315]/85 via-[#111315]/45 to-transparent" aria-hidden="true" />
                      <figcaption className="pointer-events-none absolute bottom-0 left-0 right-0 p-4">
                        <h3 className="gallery-caption text-sm sm:text-base font-semibold text-white leading-snug drop-shadow-[0_1px_3px_rgba(0,0,0,0.5)]">
                          {item.titel}
                        </h3>
                      </figcaption>
                    </motion.figure>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {current && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-[#111315]/90 backdrop-blur-sm p-4 sm:p-10"
          data-testid="gallery-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={`Bildansicht: ${current.titel}`}
          onClick={close}
        >
          <button
            onClick={close}
            data-testid="lightbox-close"
            aria-label="Bildansicht schließen"
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-[var(--ci-turquoise)] border border-white/25 text-white flex items-center justify-center transition-colors duration-200"
          >
            <X size={18} />
          </button>
          {filtered.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); step(-1); }}
                data-testid="lightbox-prev"
                aria-label="Vorheriges Bild"
                className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-[var(--ci-turquoise)] border border-white/25 text-white flex items-center justify-center transition-colors duration-200"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); step(1); }}
                data-testid="lightbox-next"
                aria-label="Nächstes Bild"
                className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-[var(--ci-turquoise)] border border-white/25 text-white flex items-center justify-center transition-colors duration-200"
              >
                <ChevronRight size={20} />
              </button>
            </>
          )}
          <figure className="max-w-[92vw] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={`${BACKEND}${current.bild_url}`}
              alt={current.titel}
              data-testid="lightbox-image"
              className="max-h-[80vh] w-auto max-w-full rounded-2xl border border-white/20 shadow-2xl object-contain"
            />
            <figcaption className="mt-5 text-center" data-testid="lightbox-caption">
              <span className="text-sm sm:text-base font-medium text-white">{current.titel}</span>
              <span className="block mt-1 font-tech text-[10px] tracking-[0.25em] uppercase text-white/50">
                {selected + 1} / {filtered.length}
              </span>
            </figcaption>
          </figure>
        </div>
      )}
    </section>
  );
}
